export function hojeISO () {
  return new Date().toISOString().slice(0, 10)
}

/** Converte LocalDate serializado como array/objeto Jackson para ISO yyyy-MM-dd */
export function normalizeISODate (value) {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value.length >= 10 ? value.slice(0, 10) : value
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }
  if (typeof value === 'object') {
    const y = value.year
    const m = value.monthValue ?? value.month
    const d = value.dayOfMonth ?? value.day
    if (y != null && m != null && d != null) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }
  return String(value)
}

export function formatDateBR (value) {
  const iso = normalizeISODate(value)
  if (!iso || iso.length < 10) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

export function normalizeContaPagar (c) {
  return {
    ...c,
    dataVencimento: normalizeISODate(c.dataVencimento),
    dataPagamento: normalizeISODate(c.dataPagamento)
  }
}

export function normalizeMovimento (m) {
  return {
    ...m,
    dataMovimento: normalizeISODate(m.dataMovimento)
  }
}

export function contaPagarStatus (c, hoje = hojeISO()) {
  if (c.isPago) return 'pago'
  if (c.dataVencimento && c.dataVencimento < hoje) return 'atrasado'
  return 'pendente'
}

export function parseRecebiveisFromCliente (cliente) {
  try {
    const raw = cliente.recebiveisJson
    const arr = typeof raw === 'string' ? JSON.parse(raw || '[]') : (raw || [])
    return (Array.isArray(arr) ? arr : []).map(r => ({
      ...r,
      clienteId: cliente.id,
      clienteNome: cliente.nome,
      clienteWhatsapp: cliente.telefoneWhatsapp || cliente.telefone_whatsapp,
      key: `${cliente.id}-${r.id}`
    }))
  } catch {
    return []
  }
}

export function flattenRecebiveis (clientes) {
  return (clientes || []).flatMap(parseRecebiveisFromCliente)
}

export function recebivelStatus (item, hoje = hojeISO()) {
  if (item.pago) return 'pago'
  if (item.dataVencimento && item.dataVencimento < hoje) return 'atrasado'
  if (item.dataVencimento === hoje) return 'hoje'
  const d7 = addDays(hoje, 7)
  if (item.dataVencimento && item.dataVencimento > hoje && item.dataVencimento <= d7) return 'prox7'
  return 'pendente'
}

function addDays (iso, days) {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function monthBounds (ref = new Date()) {
  const y = ref.getFullYear()
  const m = ref.getMonth()
  const start = new Date(y, m, 1)
  const end = new Date(y, m + 1, 0)
  return {
    inicio: start.toISOString().slice(0, 10),
    fim: end.toISOString().slice(0, 10),
    mes: m + 1,
    ano: y
  }
}

function nextMonthBounds (ref = new Date()) {
  const y = ref.getFullYear()
  const m = ref.getMonth() + 1
  const start = new Date(y, m, 1)
  const end = new Date(y, m + 1, 0)
  return { inicio: start.toISOString().slice(0, 10), fim: end.toISOString().slice(0, 10) }
}

function inRange (dateStr, inicio, fim) {
  if (!dateStr) return false
  return dateStr >= inicio && dateStr <= fim
}

function sumValor (items) {
  return items.reduce((s, i) => s + (Number(i.valor) || 0), 0)
}

export function computeKpis (recebiveis, hoje = hojeISO()) {
  const atrasados = recebiveis.filter(r => recebivelStatus(r, hoje) === 'atrasado')
  const hojeList = recebiveis.filter(r => recebivelStatus(r, hoje) === 'hoje')
  const prox7 = recebiveis.filter(r => recebivelStatus(r, hoje) === 'prox7')
  const aberto = recebiveis.filter(r => !r.pago)

  return {
    atrasados: { valor: sumValor(atrasados), qtd: atrasados.length },
    venceHoje: { valor: sumValor(hojeList), qtd: hojeList.length },
    proximos7: { valor: sumValor(prox7), qtd: prox7.length },
    totalAberto: { valor: sumValor(aberto), qtd: aberto.length }
  }
}

export function computeForecast (recebiveis, movimentos = [], contasPagar = [], ref = new Date()) {
  const hoje = hojeISO()
  const mes = monthBounds(ref)
  const prox = nextMonthBounds(ref)

  const atrasados = recebiveis.filter(r => recebivelStatus(r, hoje) === 'atrasado')
  const recebidosMes = recebiveis.filter(r =>
    r.pago && r.dataPagamento && inRange(r.dataPagamento, mes.inicio, mes.fim)
  )
  const previsaoEntradaMes = recebiveis.filter(r =>
    !r.pago && r.dataVencimento && r.dataVencimento >= hoje && inRange(r.dataVencimento, mes.inicio, mes.fim)
  )
  const previsaoProxMes = recebiveis.filter(r =>
    !r.pago && r.dataVencimento && inRange(r.dataVencimento, prox.inicio, prox.fim)
  )

  const entradasMov = (movimentos || []).filter(m =>
    m.tipo === 'ENTRADA' && inRange(m.dataMovimento, mes.inicio, mes.fim)
  )
  const gastosMov = (movimentos || []).filter(m =>
    m.tipo === 'GASTO' && inRange(m.dataMovimento, mes.inicio, mes.fim)
  )
  const contasPagasMes = (contasPagar || []).filter(c =>
    c.isPago && c.dataPagamento && inRange(c.dataPagamento, mes.inicio, mes.fim)
  )
  const contasPendentesMes = (contasPagar || []).filter(c =>
    !c.isPago && inRange(c.dataVencimento, mes.inicio, mes.fim)
  )

  const recebidosValor = sumValor(recebidosMes) + sumValor(entradasMov)
  const saidasValor = sumValor(gastosMov) + sumValor(contasPagasMes)

  return {
    mesAtual: {
      atrasados: { valor: sumValor(atrasados), qtd: atrasados.length },
      recebidos: { valor: recebidosValor, qtd: recebidosMes.length + entradasMov.length },
      previsaoEntrada: { valor: sumValor(previsaoEntradaMes), qtd: previsaoEntradaMes.length },
      saldoLiquido: { valor: recebidosValor - saidasValor },
      contasPendentesMes: { valor: sumValor(contasPendentesMes), qtd: contasPendentesMes.length }
    },
    proximoMes: {
      previsaoRecebiveis: { valor: sumValor(previsaoProxMes), qtd: previsaoProxMes.length }
    }
  }
}

export function filterByTab (items, tab, hoje = hojeISO()) {
  if (tab === 'pagos') return items.filter(r => r.pago)
  if (tab === 'atrasados') return items.filter(r => recebivelStatus(r, hoje) === 'atrasado')
  return items.filter(r => !r.pago && recebivelStatus(r, hoje) !== 'atrasado')
}

export function filterFinanceiroItems (items, { search, inicio, fim, dateField = 'dataVencimento', searchFields = ['nome', 'descricao'] }) {
  let out = items || []
  if (search?.trim()) {
    const q = search.trim().toLowerCase()
    out = out.filter(item =>
      searchFields.some(f => String(item[f] || '').toLowerCase().includes(q))
    )
  }
  if (inicio) out = out.filter(item => item[dateField] && item[dateField] >= inicio)
  if (fim) out = out.filter(item => item[dateField] && item[dateField] <= fim)
  return out
}

export function filterRecebiveisList (items, { search, inicio, fim, tab }) {
  const hoje = hojeISO()
  let out = filterByTab(items, tab, hoje)
  return filterFinanceiroItems(out, {
    search,
    inicio,
    fim,
    dateField: 'dataVencimento',
    searchFields: ['clienteNome', 'descricao']
  })
}

export function sortFinanceiroItems (items, sortBy, { dateField = 'dataVencimento', hoje = hojeISO(), statusFn } = {}) {
  const list = [...items]
  if (sortBy === 'valorDesc') {
    return list.sort((a, b) => (Number(b.valor) || 0) - (Number(a.valor) || 0))
  }
  if (sortBy === 'atrasoDesc' && statusFn) {
    return list.sort((a, b) => {
      const sa = statusFn(a, hoje) === 'atrasado' ? 1 : 0
      const sb = statusFn(b, hoje) === 'atrasado' ? 1 : 0
      if (sb !== sa) return sb - sa
      return String(a[dateField] || '').localeCompare(String(b[dateField] || ''))
    })
  }
  return list.sort((a, b) => String(a[dateField] || '').localeCompare(String(b[dateField] || '')))
}

export function sortRecebiveis (items, sortBy, hoje = hojeISO()) {
  return sortFinanceiroItems(items, sortBy, { dateField: 'dataVencimento', hoje, statusFn: recebivelStatus })
}

export function defaultPeriodoMes () {
  const m = monthBounds()
  return { inicio: m.inicio, fim: m.fim }
}
